import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Droplets, Truck } from "lucide-react";

const CERAMIC_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a08e3a5b-1647-495d-beb7-38e04e36fd68/images/3e4420a9b31417de87637c6877ac0791e107ecc83daa2e6c6f49a152a590cac5.png";

export default function Features() {
  return (
    <section id="features" className="relative bg-[#050505] py-28 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16 max-w-2xl">
          <span className="text-blue-500 uppercase text-xs tracking-[0.3em] font-semibold mb-4 block">
            · 04 The Difference
          </span>
          <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light">
            Detailing isn't a wash.
            <br />
            <span className="font-medium">It's a craft.</span>
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[200px]">
          {/* Big card with image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="col-span-12 md:col-span-7 row-span-2 relative overflow-hidden bg-[#0a0a0c] border border-white/8 group"
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{ backgroundImage: `url(${CERAMIC_IMG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <Droplets className="text-blue-400 mb-4" size={28} />
              <h3 className="font-heading text-white text-2xl md:text-3xl font-medium tracking-tight mb-2">
                Ceramic protection that lasts years, not weeks
              </h3>
              <p className="text-zinc-400 text-sm md:text-base max-w-md">
                Industry-grade 9H coatings bonded by hand. Hydrophobic, UV-stable, scratch-resistant.
              </p>
            </div>
          </motion.div>

          {/* Stat card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="col-span-6 md:col-span-5 row-span-1 bg-blue-600 p-8 flex flex-col justify-between"
          >
            <ShieldCheck className="text-white" size={28} />
            <div>
              <p className="font-heading text-white text-5xl md:text-6xl tracking-tighter font-light">
                100%
              </p>
              <p className="text-blue-100 text-sm mt-1">Satisfaction or we redo it. No questions.</p>
            </div>
          </motion.div>

          {/* Mobile card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-6 md:col-span-5 row-span-1 bg-[#0a0a0c] border border-white/8 p-8 flex flex-col justify-between hover:border-white/20 transition-colors"
          >
            <Truck className="text-blue-400" size={28} />
            <div>
              <h4 className="font-heading text-white text-xl md:text-2xl font-medium tracking-tight">
                We come to you
              </h4>
              <p className="text-zinc-500 text-sm mt-1">
                Fully self-contained mobile studio. Water, power, products — included.
              </p>
            </div>
          </motion.div>

          {/* Quote card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="col-span-12 md:col-span-7 row-span-1 bg-[#0a0a0c] border border-white/8 p-8 md:p-10 flex flex-col justify-center"
          >
            <Sparkles className="text-blue-400 mb-4" size={24} />
            <p className="font-heading text-white text-xl md:text-2xl tracking-tight font-light leading-snug">
              <span className="text-zinc-500">"</span>
              Most washes are scratching your paint. Our 2-bucket method, microfiber-only contact,
              and pH-neutral foam protect the finish — every time.
              <span className="text-zinc-500">"</span>
            </p>
          </motion.div>

          {/* Pricing card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="col-span-12 md:col-span-5 row-span-1 bg-[#0a0a0c] border border-white/8 p-8 flex flex-col justify-center"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Booked Today</p>
            <div className="flex items-baseline gap-3 mb-2">
              <p className="font-heading text-white text-4xl md:text-5xl tracking-tighter font-light">
                17
              </p>
              <p className="text-zinc-500 text-sm">cars across the city</p>
            </div>
            <div className="w-full h-1 bg-white/5 mt-3">
              <div className="h-1 bg-blue-500" style={{ width: "72%" }} />
            </div>
            <p className="text-zinc-600 text-xs mt-2">72% of weekly slots filled</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
